import { useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DialogLoading from '../../../components/feedback/DialogLoading';
import { useGetCompany } from '../../../hooks/useCompany';
import { useRedeemReward } from '../../../hooks/useReward';
import { useCartStore } from '../../../stores/cartStore';
import { useDialogStore } from '../../../stores/dialogStore';
import { useUserStore } from '../../../stores/userStore';

const valueStyles = {
  padding: '5px 10px',
  backgroundColor: '#F5F5F5',
  borderRadius: '4px',
};

const CartAddress = ({ disabledButton }) => {
  const navigate = useNavigate();

  const companyInfo = useUserStore((state) => state.companyInfo);
  const userInfo = useUserStore((state) => state.userInfo);
  const setCompanyInfo = useUserStore((state) => state.setCompanyInfo);
  const redeemLists = useCartStore((state) => state.cart);
  const setRedeemLists = useCartStore((state) => state.setCart);
  const openDialog = useDialogStore((state) => state.openDialog);

  const { mutateAsync: redeemReward, isPending: isRedeeming } = useRedeemReward();

  const { refetch: refetchCompany } = useGetCompany({
    enabled: false,
    id: companyInfo?._id,
  });

  const isOwner = useMemo(() => {
    if (!companyInfo || !userInfo) return false;
    return companyInfo.user_id._id === userInfo._id;
  }, [companyInfo, userInfo]);

  const handleRedeemReward = async () => {
    if (!companyInfo?.address?.length || !redeemLists.length) return;

    try {
      const dto = redeemLists.map(({ quantity, reward_id }) => ({ quantity, reward_id }));
      const response = await redeemReward({ dto });

      if (response.Code === '20100') {
        openDialog({
          title: 'สำเร็จ',
          message: response.Message,
          variant: 'success',
          onCloseCallback: () => {
            navigate('/reward');
          },
        });
        setRedeemLists([]);

        const { data: companyResponse } = await refetchCompany();
        if (companyResponse?.Code === '20000') {
          setCompanyInfo(companyResponse.Data.data[0]);
        }
      }
    } catch (err) {
      setRedeemLists([]);
      openDialog({
        title: 'เกิดข้อผิดพลาด',
        message: err?.response?.data?.Message || 'เกิดข้อผิดพลาดในการแลกของรางวัล',
        variant: 'error',
      });
    }
  };

  return (
    <Box>
      {/* Title */}
      <Box
        sx={{
          backgroundColor: '#E1000C',
          color: 'white',
          padding: '2px 10px',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: '20px',
        }}
      >
        <Typography component={'h1'} sx={{ fontSize: '1.75rem', fontWeight: '500' }}>
          โปรดตรวจสอบที่อยู่สำหรับจัดส่ง
        </Typography>
      </Box>

      {/* Address Info */}
      <Box
        sx={{
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: 'white',
        }}
      >
        {!companyInfo?.address && (
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        )}

        {companyInfo?.address?.map(
          (item) =>
            item.default && (
              <Grid
                key={item._id}
                container
                rowGap={1}
                columnGap={2}
                sx={{ position: 'relative', paddingRight: '2rem' }}
              >
                <AddressRow label="ชื่อ - นามสกุล" value={item.name} />
                <AddressRow label="เบอร์โทรศัพท์" value={item.phonenumber} />
                <AddressRow label="บ้านเลขที่ / หมู่บ้าน :" value={item.detail} />
                <AddressRow
                  label="ซอย / ถนน :"
                  value={`${item.alley?.replace(/^\s*-\s*$/, '') ?? ''} ${item.street_name?.replace(/^\s*-\s*$/, '') ?? ''}`.trim()}
                />
                <AddressRow label="ตำบล" value={item.subdistrict} />
                <AddressRow label="อำเภอ" value={item.district} />
                <AddressRow label="จังหวัด" value={item.province} />
                <AddressRow label="รหัสไปรษณีย์" value={item.zip_code} />

                {isOwner && (
                  <IconButton
                    aria-label="แก้ไขที่อยู่"
                    onClick={() => navigate('/address')}
                    sx={{ padding: '0', position: 'absolute', top: '0px', right: '0' }}
                  >
                    <Edit sx={{ color: '#004B61' }} />
                  </IconButton>
                )}
              </Grid>
            )
        )}

        {/* Add address button */}
        {companyInfo?.address?.length === 0 && isOwner && (
          <Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: '500',
                color: '#262626',
                marginBottom: '10px',
              }}
            >
              เพิ่มที่อยู่
            </Typography>
            <Typography sx={{ fontSize: '.875rem', fontWeight: '400', color: '#737373' }}>
              กรุณาเพิ่มที่อยู่สำหรับจัดส่งสินค้าอย่างน้อย 1 ที่อยู่
              เพื่อใช้ประกอบการดำเนินการจัดส่ง
            </Typography>
            <IconButton
              aria-label="เพิ่มที่อยู่"
              onClick={() => navigate('/address/add')}
              sx={{
                width: '100%',
                height: '45px',
                marginTop: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': { backgroundColor: '#fafafa' },
              }}
            >
              <AddIcon sx={{ fontSize: '1.5rem', color: '#004B61' }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Confirm Button */}
      <Button
        onClick={handleRedeemReward}
        disabled={disabledButton || isRedeeming}
        sx={{
          width: '150px',
          height: '45px',
          fontSize: '1.25rem',
          fontWeight: '500',
          display: 'block',
          margin: '0 auto',
          backgroundColor: '#E1000C',
          color: 'white',
          borderRadius: '6px',

          '&.Mui-disabled': {
            color: '#525252',
            backgroundColor: '#f5f5f5',
          },

          '&:hover': {
            color: 'white',
            backgroundColor: '#E1000C',
            opacity: '.85',
          },
        }}
      >
        ยืนยัน
      </Button>

      {/* Loading Dialog */}
      <DialogLoading open={isRedeeming} message={'กำลังส่งข้อมูล'} />
    </Box>
  );
};

const AddressRow = ({ label, value }) => (
  <>
    <Grid item xs={4}>
      <LabelField>{label}</LabelField>
    </Grid>
    <Grid item xs={7} alignSelf="center" sx={valueStyles}>
      <ValueField>{value}</ValueField>
    </Grid>
  </>
);

const LabelField = ({ children }) => (
  <Typography sx={{ fontSize: '1.25rem', fontWeight: '500', color: '#014B60', textAlign: 'right' }}>
    {children}
  </Typography>
);

const ValueField = ({ children }) => (
  <Typography sx={{ fontSize: '1.25rem', fontWeight: '500', color: '#E1000C', minHeight: '30px' }}>
    {children}
  </Typography>
);

export default CartAddress;
